import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { Package } from '@vendasta/marketplace-packages';
import { App } from '@galaxy/marketplace-apps';
import {
  addMenuItemsToSalesPackages,
  addMenuItemsToSalesProduct,
  addStatusesToSalesPackages,
  addStatusesToSalesProduct,
  buildSalesPackages,
  buildSalesProductFromProduct,
  PackageItemActivationStatus,
  SalesPackage,
  SalesProduct,
} from './sales-package';
import { ListIndent, MenuItemClickedInterface } from './package-dropdown-item/package-dropdown-item.component';
import { ActivationFailureDetails } from '@vendasta/order-fulfillment/lib/_internal/objects';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';

interface OrderLengthsForToggling {
  packages: number;
  products: number;
}

@Component({
  selector: 'sales-ui-sales-order-package-dropdown',
  animations: [
    trigger('listAnimationStart', [
      state('void', style({ height: '0px' })),
      state('*', style({ height: '*' })),
      transition('void => *', [animate('0.25s ease')]),
      transition('* => void', [animate('0.25s ease')]),
    ]),
  ],
  templateUrl: 'package-dropdown.component.html',
  styleUrls: ['../../../shared/shared-styles.scss', './package-dropdown.component.scss'],
  standalone: false,
})
export class SalesOrderPackageDropdownComponent implements OnChanges {
  // whether to show the contents in it's own card or a closed expansion panel
  @Input() compactView = false;
  // packages in dropdown
  @Input() packages: Package[] = [];
  // apps that are in the given packages
  @Input() appsInPackages: App[] = [];
  // apps not associated with a package
  @Input() standAloneApps: App[] = [];
  // statuses for all products and addons
  @Input() statuses: PackageItemActivationStatus[] = [];
  @Input() title: string;
  @Input() showMenuItems = false;
  @Input() activationFailureDetails: ActivationFailureDetails[];

  @Output() menuItemClicked: EventEmitter<MenuItemClickedInterface> = new EventEmitter<MenuItemClickedInterface>();

  packageProductIndent = ListIndent.INDENT_ONE;
  packageAddonWithoutProductIndent = ListIndent.INDENT_ONE;
  packageAddonWithProductIndent = ListIndent.INDENT_TWO;

  orderItemLengths: OrderLengthsForToggling = {
    packages: 0,
    products: 0,
  };

  salesPackages: SalesPackage[] = [];
  salesProducts: SalesProduct[] = [];
  packageToggles: boolean[] = [];
  productToggles: boolean[] = [];

  constructor(private translateService: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.packages = changes.packages ? changes.packages.currentValue : this.packages;
    this.appsInPackages = changes.appsInPackages ? changes.appsInPackages.currentValue : this.appsInPackages;
    this.standAloneApps = changes.standAloneApps ? changes.standAloneApps.currentValue : this.standAloneApps;
    this.statuses = changes.statuses ? changes.statuses.currentValue : this.statuses;
    this.title = changes.title ? changes.title.currentValue : this.title;

    this.orderItemLengths = {
      packages: this.packages.length,
      products: this.appsInPackages.length,
    };
    this.salesPackages = addMenuItemsToSalesPackages(
      addStatusesToSalesPackages(
        buildSalesPackages(this.packages, this.appsInPackages),
        this.statuses,
        this.activationFailureDetails,
      ),
    );
    this.salesProducts = this.standAloneApps.map((a) =>
      addMenuItemsToSalesProduct(
        addStatusesToSalesProduct(buildSalesProductFromProduct(a, []), this.statuses, this.activationFailureDetails),
      ),
    );
  }

  public openAllDropdowns(): void {
    for (let x = 0; x < this.orderItemLengths.packages; x++) {
      this.packageToggles[x] = true;
    }
    for (let x = 0; x < this.orderItemLengths.products; x++) {
      this.productToggles[x] = true;
    }
  }

  public togglePackage(indexToToggle: number): void {
    this.packageToggles[indexToToggle] = !this.packageToggles[indexToToggle];
  }

  public standAloneProductWithParentDescription(product: SalesProduct): string {
    if (!product.parentRequirements.enabled) {
      return '';
    }

    return this.translateService.instant('FRONTEND.STORE.REQUIRES_DESCRIPTOR', {
      requirementName: product.parentRequirements.parentDetails.name,
    });
  }

  public packageDescription(pkg: SalesPackage): string {
    const productsCount = pkg.salesProducts ? pkg.salesProducts.length : 0;
    return this.translateService.instant('FRONTEND.STORE.CONTAINS_NUM_ITEMS', {
      itemCount: productsCount + pkg.totalAddons,
    });
  }

  menuItemClick(event: MenuItemClickedInterface): void {
    this.menuItemClicked.emit(event);
  }

  getPreviousEditionName(product: SalesProduct): string {
    if (
      product &&
      product.activationStatus &&
      product.activationStatus.isEditionChange &&
      product.activationStatus.editionId !== product.activationStatus.previousEditionId
    ) {
      const previousEdition = (product.editionInformation?.editions || []).find((edition) => {
        return edition?.appKey?.editionId === product.activationStatus.previousEditionId;
      });

      return previousEdition?.name || '';
    }

    return '';
  }
}
